import { watch } from 'vue';
import type { Router } from 'vue-router';
import { defaultSettings } from '@/composables/use-settings';
import { settingsLoadPromise } from '@/composables/use-update-settings';
import { BOOT_TEST_GLOBALS } from '@/shared/constants/boot-test-globals';
import { MODULE_REGISTRY, getModuleSettingsKey, getModuleRoutingSettingsSnapshot } from '@/app-router/citizen/module-registry';
import { syncModuleRoutes, reconcileRouterWithBrowserUrl } from '@/app-router/citizen/index';
import citizenFixedRoutes from '@/app-router/citizen/fixed-routes.generated.js';

let resolveModuleRoutesReady: (() => void) | null;
export const moduleRoutesReadyPromise = new Promise<void>((resolve) => {
  resolveModuleRoutesReady = resolve;
});

function markModuleRoutesReady() {
  resolveModuleRoutesReady?.();
  resolveModuleRoutesReady = null;

  if (import.meta.env.MODE === 'test' && typeof window !== 'undefined') {
    (window as unknown as Record<string, boolean>)[BOOT_TEST_GLOBALS.MODULE_ROUTES_READY] = true;
  }
}

export function useModuleRoutes(router: Router) {
  let routerReady = false;

  const applyModuleRoutes = () => {
    syncModuleRoutes(router, citizenFixedRoutes, getModuleRoutingSettingsSnapshot(defaultSettings));
  };

  watch(
    () =>
      MODULE_REGISTRY.flatMap((module) => [
        defaultSettings[getModuleSettingsKey(module.id, 'enabled')],
        defaultSettings[getModuleSettingsKey(module.id, 'path')],
        defaultSettings[`citizen_module_${module.id}`],
      ]),
    () => {
      if (routerReady) {
        applyModuleRoutes();
      }
    },
    { deep: true },
  );

  router
    .isReady()
    .then(async () => {
      try {
        await settingsLoadPromise;
        routerReady = true;
        await syncModuleRoutes(router, citizenFixedRoutes, getModuleRoutingSettingsSnapshot(defaultSettings));
        await reconcileRouterWithBrowserUrl(router);
      } catch (error) {
        console.error('[module-routes] Failed to sync module routes after router ready:', error);
      } finally {
        markModuleRoutesReady();
      }
    })
    .catch((error) => {
      console.error('[module-routes] router.isReady() rejected:', error);
      markModuleRoutesReady();
    });
}
